/**
 * The sidebar's top-left brand (#2387): the current tenant's logo and name.
 *
 * The name always comes from the tenant. A tenant whose logo can be shown gets
 * its logo; one without (no logo, or the logo could not be fetched) gets a
 * primary-colour square carrying the first letter of its name (v4 shell,
 * #2476 S2). While the branding request loads, or after it fails, the Fastcade
 * mark and name show — the brand never blocks the page.
 */
import { useTranslation } from 'react-i18next';
import { useTenantBranding, useTenantLogoSrc } from '@hooks/useTenantBranding';

// Platform brand name: a product name, deliberately not translated.
const BRAND_NAME = 'Fastcade';
const DEFAULT_LOGO_SRC = '/logo.svg';

/** The tenant's display name, or the platform's while branding loads or failed. */
export function useBrandName(): string {
  return useTenantBranding().data?.name ?? BRAND_NAME;
}

interface BrandMarkProps {
  /** 32 in the sidebar (shadcn `size="lg"` header, #2514), 30 in the phone header (#2487 H1). */
  size: 32 | 30;
  /** `data-testid` prefix; the sidebar stays mounted beside the phone header. */
  testIdPrefix?: string;
}

const MARK_SIZE = { 32: 'size-8', 30: 'w-[30px] h-[30px]' } as const;

/**
 * The tenant's brand mark: its logo when one can be shown, else a
 * primary-colour square with the first letter of its name, else (loading or
 * failed) the Fastcade mark. One rule for the sidebar and the phone header.
 */
export function BrandMark({ size, testIdPrefix = 'sidebar-brand' }: BrandMarkProps) {
  const branding = useTenantBranding();
  const logoSrc = useTenantLogoSrc(branding.data?.logoUrl ?? null);
  const name = branding.data?.name ?? BRAND_NAME;
  const box = MARK_SIZE[size];

  if (logoSrc !== null || !branding.data) {
    return (
      <img
        data-testid={`${testIdPrefix}-logo`}
        src={logoSrc ?? DEFAULT_LOGO_SRC}
        alt={name}
        className={`${box} shrink-0 rounded-lg object-contain`}
      />
    );
  }
  return (
    <span
      data-testid={`${testIdPrefix}-initial`}
      aria-hidden="true"
      className={`grid ${box} shrink-0 place-items-center rounded-lg bg-primary ${
        size === 30 ? 'text-sm' : 'text-[13px]'
      } font-bold text-primary-foreground`}
    >
      {Array.from(name)[0]?.toUpperCase()}
    </span>
  );
}

/**
 * The contents of the sidebar's header button (#2514, shadcn sidebar-07): the
 * mark beside two lines, the tenant name over a fixed product subtitle. The
 * caller owns the `SidebarMenuButton size="lg"`, which shrinks to the mark
 * when the sidebar collapses to icons.
 */
export default function SidebarBrand() {
  const name = useBrandName();
  const { t } = useTranslation();

  return (
    <>
      <BrandMark size={32} />
      <span className="grid flex-1 text-left text-sm leading-tight">
        <span data-testid="sidebar-brand-name" className="truncate font-medium">
          {name}
        </span>
        <span
          data-testid="sidebar-brand-subtitle"
          className="truncate text-xs text-muted-foreground"
        >
          {t('nav.brandSubtitle')}
        </span>
      </span>
    </>
  );
}
